<template>
  <v-container fluid :class="{ 'mt-4': !$vuetify.breakpoint.mobile }">
    <mobile-data-list
      v-if="$vuetify.breakpoint.mobile"
      :items="statementsList || []"
      :loading="portionLoading"
      :items-per-page="itemsPerPage"
      :footer-props="footerProps"
      item-key="id"
      :headers="mobileHeaders"
      single-expand
    >
      <template #item.amountPaid="{ item }">
        <span v-if="item && item.utbk">{{ item.utbk | nf }}</span>
        <span v-else>-</span>
      </template>
      <template #expanded-actions="{ item }">
        <div class="d-flex gap-2 mt-2 mb-1" style="gap: 8px">
          <v-btn small outlined @click.stop="downloadDetails(item)">
            <v-icon left small>mdi-file-pdf-box</v-icon>
            {{ $t('statements.details') }}
          </v-btn>
          <v-btn small outlined @click.stop="downloadPlaylist(item)">
            <v-icon left small>mdi-file-excel-outline</v-icon>
            {{ $t('statements.playlist') }}
          </v-btn>
        </div>
      </template>
    </mobile-data-list>

    <data-table-scroll-hint v-else>
    <v-data-table
        class="no-select-table"
        :loading="portionLoading"
        :loading-text="$t('statements.loadingText')"
        :items="statementsList || []"
        :headers="headers"
        :items-per-page.sync="itemsPerPage"
        :page.sync="currentPage"
        item-key="id"
        :footer-props="footerProps"
    >
    <!--
        :expanded.sync="expandedRows"
        @click:row="openDetails"
        show-expand
    -->

      <template v-slot:expanded-item="{headers, item}">
        <td :colspan="headers.length">
          <v-container v-if="item">
            <v-row>
              <v-col cols="4">
                <table class="statement-table">
                  <tbody>
                  <tr
                      v-for="col in ['grossAmount', 'administrativeCharge', 'netAmount', 'serviceVAT', 'grossVAT', 'amountPaid']"
                      :key="col"
                  >
                    <td>{{  $t(`statements.columns.${col}`) }}</td>
                    <td>{{ item[col] | nf }}</td>
                  </tr>
                  </tbody>
                </table>
              </v-col>

              <v-col cols="4">
                <table class="statement-table">
                  <tbody>
                  <tr v-for="col in ['invoiceNumber', 'invoiceDateFormatted', 'paymentDateFormatted', 'paidTo']" :key="col">
                    <td>{{ $t(`statements.columns.${col}`) }}</td>
                    <td>{{ item[col] }}</td>
                  </tr>
                  </tbody>
                </table>
              </v-col>
            </v-row>
          </v-container>
        </td>
      </template>

      <template v-slot:item.amountPaid="{item}">
        <span v-if="item && item.utbk">{{ item.utbk | nf }}</span>
        <span v-else>-</span>
      </template>

      <template v-slot:item.actions="{item}">
        <div class="text-end">
          <v-btn @click.stop="downloadDetails(item)" text color="primary" :small="$vuetify.breakpoint.smAndDown">
            <v-icon left>mdi-file-pdf-box</v-icon>
            <span> {{ $t('statements.details') }} </span>
          </v-btn>
          <v-btn @click.stop="downloadPlaylist(item)" text color="primary" :small="$vuetify.breakpoint.smAndDown">
            <v-icon left>mdi-file-excel-outline</v-icon>
            <span> {{ $t('statements.playlist') }} </span>
          </v-btn>
        </div>
      </template>
    </v-data-table>
    </data-table-scroll-hint>
  </v-container>
</template>

<script>
import {mapGetters} from "vuex";
import downloadFile from "@/utils/downloadFile";
import RightsholderInitedDataDrivenMixin from "@/components/mixins/RightsholderInitedDataDrivenMixin";
import MobileDataList from "@/components/common/MobileDataList";
import DataTableScrollHint from '@/components/common/DataTableScrollHint.vue';

export default {
  name: 'Statements',
  components: { MobileDataList, DataTableScrollHint },
  data() {
    return {
      expandedRows: [],
      itemsPerPage: 50,
      currentPage: 1,
      portionLoading: false,
    }
  },
  mixins: [RightsholderInitedDataDrivenMixin],
  computed: {
    ...mapGetters('app', ['statements']),
    footerProps() {
      return {
        itemsPerPageOptions: this.$vuetify.breakpoint.mobile ? [] : [10, 25, 50],
        pageText: this.continuosAmount ? `{0} / ${this.statementsList.length - 1}+` : '$vuetify.dataFooter.pageText',
      }
    },
    headers() {
      const getHeader = (value) => ({value, text: this.$t(`statements.columns.${value}`)});
      return [
        {text: '', value: 'data-table-expand'},
        getHeader('invoiceNumber'),
        getHeader('paymentDateFormatted'),
        getHeader('paidTo'),
        {...getHeader('amountPaid'), ...{align: 'end'}},
        {value: 'actions', text: null, sortable: false,}
      ]
    },
    continuosAmount() {
      return !this.statements.finished;
    },
    mobileHeaders() {
      const h = (value) => ({ value, text: this.$t(`statements.columns.${value}`) });
      return [
        { ...h('paymentDateFormatted'), mobile: 'primary' },
        { ...h('amountPaid'),          mobile: 'secondary' },
        { ...h('invoiceNumber'),        mobile: 'tertiary' },
        h('paidTo'),
      ];
    },
    statementsList() {
      if (!this.statements.list) {
        return [];
      }
      return this.statements.list 
      //.concat(this.statements.finished ? [] : [null]);
    },
    lastPage() {
      return Math.ceil((this.statementsList.length - Number(this.continuosAmount)) / this.itemsPerPage);
    },
  },

  watch: {
    currentPage() {
      if (this.currentPage === (this.lastPage + 1)) {
        if (this.continuosAmount && this.lastPage > 0) {
          this.loadStatementsList()
        }
      }
    }
  },

  methods: {

    reloadItems() {
      this.loadStatementsList(true);
    },

    openDetails(item) {
      if (this.expandedRows.includes(item)) {
        this.expandedRows = this.expandedRows.filter(i => i !== item);
      } else {
        this.expandedRows.push(item);
      }
    },

    loadStatementsList(force = false) {
      if (force) {
        this.currentPage = 1;
      }

      this.portionLoading = true;
      this.$store.dispatch('app/loadStatements', {reload: force})
        .then(() => {
          this.portionLoading = false;
        });
    },

    downloadDetails(item) {
      this.$store.dispatch('app/downloadStatementDetails', item.invoiceNumber).then((blobUrl) => {
        downloadFile(`invoice_${item.invoiceNumber}.pdf`, blobUrl);
      })
    },

    downloadPlaylist(item) {
      this.$store.dispatch('app/downloadStatementPlaylist', item.invoiceNumber).then((blobUrl) => {
        downloadFile(`playlist_${item.invoiceNumber}.xlsx`, blobUrl);
      })
    }
  },

}
</script>

<style scoped lang="scss">
.statement-table {
  width: 100%;

  td:first-child {
    font-weight: bold;
    padding-right: 1em;
  }

  td:last-child {
    text-align: right;
  }
}
</style>